import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Home, Compass, Heart, PlusSquare, User, Menu, X, MessageCircle, Zap } from 'lucide-react';
import { currentUser } from '../../data/mockData';
import { useAuthStore } from '../../store/authStore';
import { SearchModal } from '../search/SearchModal';
import { LogoutButton } from '../ui/LogoutButton';

export const Header: React.FC = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const handleSearchClick = () => {
    setShowSearchModal(true);
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <span className="text-xl font-bold text-purple-600">PetSocial</span>
                <span className="ml-1 text-orange-500">🐾</span>
              </Link>
              
              {/* Powered by Bolt */}
              <div className="hidden md:flex items-center ml-4 px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full">
                <Zap className="h-3 w-3 text-purple-600 mr-1" />
                <span className="text-xs font-medium text-purple-700">Powered by Bolt</span>
              </div>
            </div>

            {/* Search - Desktop */}
            <div className="hidden md:block max-w-xs w-full mx-6">
              <button
                onClick={handleSearchClick}
                className="w-full flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <Search className="h-4 w-4 mr-2" />
                <span>Search pets, breeds...</span>
              </button>
            </div>

            {/* Navigation - Desktop */}
            <nav className="hidden md:flex items-center space-x-4">
              <Link to="/" className="p-1 text-gray-700 hover:text-purple-600 transition-colors">
                <Home className="h-6 w-6" />
              </Link>
              <Link to="/explore" className="p-1 text-gray-700 hover:text-purple-600 transition-colors">
                <Compass className="h-6 w-6" />
              </Link>
              <Link to="/messages" className="p-1 text-gray-700 hover:text-purple-600 transition-colors">
                <MessageCircle className="h-6 w-6" />
              </Link>
              <Link to="/notifications" className="p-1 text-gray-700 hover:text-purple-600 transition-colors">
                <Heart className="h-6 w-6" />
              </Link>
              <Link to="/create" className="p-1 text-gray-700 hover:text-purple-600 transition-colors">
                <PlusSquare className="h-6 w-6" />
              </Link>
              <Link to="/profile" className="relative">
                <img 
                  src={currentUser.profileImage} 
                  alt="Profile" 
                  className="h-8 w-8 rounded-full object-cover border border-gray-200"
                />
              </Link>
              <LogoutButton variant="icon" showConfirmation={true} />
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={handleSearchClick}
                className="p-2 text-gray-700 mr-2"
              >
                <Search className="h-5 w-5" />
              </button>
              <button 
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 text-gray-700"
              >
                {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Powered by Bolt - Mobile */}
          <div className="md:hidden flex justify-center pb-2">
            <div className="flex items-center px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 rounded-full">
              <Zap className="h-3 w-3 text-purple-600 mr-1" />
              <span className="text-xs font-medium text-purple-700">Powered by Bolt</span>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden bg-white border-t border-gray-200 absolute w-full">
            <nav className="flex flex-col">
              <Link to="/" className="px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center">
                <Home className="h-5 w-5 mr-3" />
                <span>Home</span>
              </Link>
              <Link to="/explore" className="px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center">
                <Compass className="h-5 w-5 mr-3" />
                <span>Explore</span>
              </Link>
              <Link to="/messages" className="px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center">
                <MessageCircle className="h-5 w-5 mr-3" />
                <span>Messages</span>
              </Link>
              <Link to="/notifications" className="px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center">
                <Heart className="h-5 w-5 mr-3" />
                <span>Notifications</span>
              </Link>
              <Link to="/create" className="px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center">
                <PlusSquare className="h-5 w-5 mr-3" />
                <span>Create</span>
              </Link>
              <Link to="/profile" className="px-4 py-3 text-gray-700 hover:bg-gray-100 flex items-center">
                <User className="h-5 w-5 mr-3" />
                <span>Profile</span>
              </Link>
              <LogoutButton variant="text" showConfirmation={true} />
            </nav>
          </div>
        )}
      </header>

      {/* Search Modal */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
      />
    </>
  );
};